import { useMutation } from "@apollo/client";
import { Drawer, message, Modal } from "antd";
import dayjs from "dayjs";
import React, { useContext, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LuPencil, LuTrash2 } from "react-icons/lu";
import { UserContext } from "../../../amplify/authenticator-provider";
import { DELETE_SCHEDULED_MESSAGE } from "../../../graphql/mutation";
import useLang from "../../../hooks/useLang";
import { closeDialog, openDialog } from "../../../library/helpers";
import { removeScheduledMessage } from "../../../redux/slices/leadSlice";
import ScheduleMessagesPopup from "./scheduleMessagesPopup";

const localizedFormat = require("dayjs/plugin/localizedFormat");

dayjs.extend(localizedFormat);

const ViewScheduledMessagesPopup = props => {
  const {
    selectedLead,
    getMessages,
    messageText,
    scheduledMessageDrawer,
    setscheduledMessageDrawer,
  } = props;
  const [lang] = useLang();
  const dispatch = useDispatch();
  const { user } = useContext(UserContext);
  const { leadScheduledMessages: scheduledMessages } = useSelector(state => state?.lead);
  // Determine the correct timezone based on context
  const timeZone = useMemo(() => {
    const currentLocationId = sessionStorage.getItem("currentLocationId");

    // Multi-location context: use user's location timezone
    if (currentLocationId?.includes(",")) {
      return user?.locations?.find(item => item.location.id === user?.locationId)?.location
        ?.timezone;
    }

    // Single location context: use session location timezone
    const sessionLocation = user?.locations?.find(item => item.location.id === currentLocationId);
    return sessionLocation?.location?.timezone;
  }, [user?.locations, user?.locationId]);
  const timeZoneObj = timeZone;

  return (
    <Drawer
      open={scheduledMessageDrawer.open}
      width="50%"
      className="normal-popup"
      title="Scheduled Messages"
      onClose={() => setscheduledMessageDrawer({ open: false, messages: null })}
    >
      <div>
        <div className="flex-col-centered gap-1">
          {scheduledMessages.map((item, index) => {
            return (
              <ScheduledMessage
                key={index}
                index={index}
                item={item}
                timeZone={timeZoneObj}
                scheduledMessages={scheduledMessages}
                getMessages={getMessages}
                selectedLead={selectedLead}
                lang={lang}
                setscheduledMessageDrawer={setscheduledMessageDrawer}
              />
            );
          })}
        </div>
      </div>
    </Drawer>
  );
};

export default ViewScheduledMessagesPopup;
function ScheduledMessage({
  index,
  item,
  timeZone,
  scheduledMessages,
  getMessages,
  selectedLead,
  lang,
  setscheduledMessageDrawer,
}) {
  const dispatch = useDispatch();
  const [deleteScheduledMessage, { loading: deleteLoading }] =
    useMutation(DELETE_SCHEDULED_MESSAGE);
  const [scheduleMessageForm, setscheduleMessageForm] = useState({
    open: false,
  });
  const [stopCampaignConfirmation, setstopCampaignConfirmation] = useState({
    visible: false,
    item: {},
  });
  const [expanded, setexpanded] = useState(false);
  const handleDeleteMessage = async (index, record) => {
    try {
      const variables = {
        id: record?.id,
        timezone: dayjs.tz.guess(),
      };
      const resp = await deleteScheduledMessage({
        variables,
      });
      if (resp?.data?.deleteScheduledMessage?.statusCode === 200) {
        message.success("Scheduled message deleted successfully");
        if (index > -1) {
          dispatch(removeScheduledMessage(index));
        }
        getMessages({
          variables: {
            leadId: selectedLead?.id,
            page: 1,
          },
        });
        setscheduledMessageDrawer({ open: false });
      } else {
        console.error(resp?.data?.deleteScheduledMessage?.message);
        message.error("Failed to delete scheduled message. Please try again or contact support.");
      }
      closeDialog("confirmDialog");
    } catch (err) {
      console.error(
        "Failed to delete scheduled message. Please try again or contact support. err =",
        err
      );
      message.error("Failed to delete scheduled message. Please try again or contact support.");
    }
  };
  const maxCount = 270;
  const handleDeletePopup = () => {
    openDialog("confirmDialog", {
      handleConfirm: () => handleDeleteMessage(index, item),
      label: lang.deleteSchMsgConfPopupTitle,
      loading: deleteLoading,
      content: lang.deleteSchMsgConfPopupBody,
      confirmBtnText: lang.ok,
    });
  };

  return (
    <div
      key={index}
      className="flex-col-centered rounded-[4px] p-3 border-[1px] border-solid border-grey-200"
    >
      <div className="flex-row-centered justify-between items-center">
        <div className="flex-row-centered items-center gap-1">
          <span className="text-sm font-[500] text-grey-700">By {item.user.fullName}</span>
          <div className="flex-row-centered items-center gap-0.5">
            <div className="p-0.5 rounded-full bg-grey-500" />
            <span className="text-xs font-[400] text-grey-500">
              Send on{" "}
              {dayjs(item?.taskSendDatetime)
                .utc(true)
                .tz(timeZone?.value)
                .format("MMM DD, hh:mm A")}{" "}
              {timeZone?.shortName || dayjs.tz.guess().shortName || ""}
            </span>
          </div>
        </div>
        <div className="flex-row-centered items-center gap-[13px]">
          <LuPencil
            size={16}
            strokeWidth={1.75}
            className="cursor-pointer text-grey-500"
            onClick={() =>
              item?.isTaskExecuted
                ? () => message.error("This message is already sent, cannot edit it now")
                : setscheduleMessageForm({ open: true })
            }
          />
          <LuTrash2
            size={16}
            strokeWidth={1.75}
            className="cursor-pointer text-error-500"
            onClick={() =>
              item?.isTaskExecuted
                ? () => message.error("This message is already sent, cannot delete it now")
                : handleDeletePopup()
            }
          />
        </div>
      </div>
      <span className="text-sm font-[400] text-grey-700">
        {item.message.length > maxCount && !expanded
          ? `${item.message.slice(0, maxCount)}...`
          : item.message}
        {item.message.length > maxCount && (
          <span className="text-brand-500 cursor-pointer" onClick={() => setexpanded(!expanded)}>
            {expanded ? "less" : "more"}
          </span>
        )}
      </span>
      <Modal
        footer={null}
        className="normal-popup"
        onCancel={() => setscheduleMessageForm({ open: false })}
        title="Edit scheduled message"
        open={scheduleMessageForm.open}
      >
        <ScheduleMessagesPopup
          messageData={item}
          close={() => setscheduleMessageForm({ open: false })}
          mode="EDIT"
          scheduledMessages={scheduledMessages}
          selectedLead={selectedLead}
          getMessages={getMessages}
        />
      </Modal>
    </div>
  );
}
